import axios from 'axios';

/**
 * Bulletproof API instance for MusB Diagnostic.
 * Handles both Netlify Proxy (/api) and Direct Render URL (via env var).
 */

// Diagnostic console logging: development builds only. Production must never
// print request URLs / error payloads to the user's console.
const API_DEBUG = process.env.NODE_ENV !== 'production';
const getBaseURL = () => {
  // Use the Netlify environment variable (prefixed for React)
  let url = process.env.REACT_APP_API_URL || '';
  
  // Strip trailing slashes to avoid //api/... double-slashing
  return url.replace(/\/$/, '');
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 60000, // 60 second timeout for Render cold boots
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with cross-origin requests
});

// Global Emoji Blocker Interceptor
api.interceptors.request.use((config) => {
  const hasEmoji = (data) => {
    if (typeof data === 'string') {
      for (let i = 0; i < data.length; i++) {
        const cp = data.codePointAt(i);
        if (cp > 0xffff || (cp >= 0x2600 && cp <= 0x27bf)) {
          return true;
        }
        if (cp > 0xffff) {
          i++; // Skip second half of surrogate pair
        }
      }
      return false;
    }
    if (data && typeof data === 'object') {
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          if (hasEmoji(key) || hasEmoji(data[key])) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const isFileUpload = config.data instanceof FormData;
  if (!isFileUpload && hasEmoji(config.data)) {
    const errorMsg = 'Emojis are not permitted in any form fields.';
    alert(errorMsg);
    throw new Error(errorMsg);
  }
  if (isFileUpload) {
    // Crucial: Delete explicit Content-Type to allow browser/axios to generate multipart/form-data with boundary
    delete config.headers['Content-Type'];
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor for production debugging & Automatic Token Injection
api.interceptors.request.use((config) => {
  // 1. Skip token injection for login/signup endpoints
  const isAuthEndpoint = config.url.endsWith('/login/') || config.url.endsWith('/signup/');
  
  if (!isAuthEndpoint) {
    // If caller explicitly provided an Authorization Bearer header, preserve it
    if (config.headers.Authorization && config.headers.Authorization.startsWith('Bearer ')) {
      if (API_DEBUG) console.log(`🌐 [API Request] ${config.method.toUpperCase()} ${config.url}`);
      return config;
    }

    const labToken = localStorage.getItem('lab_token') || localStorage.getItem('staff_token');
    const doctorToken = localStorage.getItem('doctor_token');
    const techToken = localStorage.getItem('technician_token');
    const phlebToken = localStorage.getItem('phleb_token');
    const employerToken = localStorage.getItem('employer_token') || localStorage.getItem('token') || localStorage.getItem('staff_token');
    const researchToken = localStorage.getItem('research_token');
    const superAdminToken = localStorage.getItem('super_admin_token') || localStorage.getItem('admin_token');
    const hubToken = localStorage.getItem('hub_token');
    const patientToken = localStorage.getItem('patient_token');
    const reqUrl = config.url || '';
    let token = null;

    if (reqUrl.includes('/api/labs/')) {
      token = labToken || techToken || superAdminToken;
    } else if (reqUrl.includes('/api/doctor/')) {
      token = doctorToken || labToken || superAdminToken;
    } else if (reqUrl.includes('/api/technician/')) {
      token = techToken || labToken || superAdminToken;
    } else if (reqUrl.includes('/api/staff/')) {
      token = labToken || techToken || superAdminToken;
    } else if (reqUrl.includes('/api/superadmin/')) {
      token = superAdminToken;
    } else if (reqUrl.includes('/api/phleb/hubs/')) {
      token = hubToken;
    } else if (reqUrl.includes('/api/phleb/') || reqUrl.includes('/api/phlebotomists/')) {
      token = phlebToken;
    } else if (reqUrl.includes('/api/employers/')) {
      token = employerToken;
    } else if (reqUrl.includes('/api/research/')) {
      token = researchToken;
    } else if (reqUrl.includes('/api/patients/')) {
      token = patientToken;
    } else if (reqUrl.includes('/api/chat/')) {
      token = patientToken || phlebToken;
    } else {
      token = labToken || doctorToken || techToken || phlebToken || patientToken || employerToken || researchToken || hubToken || superAdminToken;
    }

    const isPublicPatientEndpoint = reqUrl.includes('/autocomplete-address/') || reqUrl.includes('/place-details/');
    const isValidJwt = token && typeof token === 'string' && token !== 'null' && token !== 'undefined' && token !== 'true' && token !== 'false' && token.length > 20 && token.includes('.');
    if (isValidJwt && !isPublicPatientEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
  }

  if (API_DEBUG) console.log(`🌐 [API Request] ${config.method.toUpperCase()} ${config.url}`);
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (API_DEBUG) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message;
      console.error('❌ [API Error]', {
        url: error.config?.url,
        status: error.response?.status,
        message: errorMsg,
        data: error.response?.data
      });
    }
    return Promise.reject(error);
  }
);

// In-memory cache store for GET requests
const apiCache = {
  store: {},
  get(url) {
    const item = this.store[url];
    if (!item) return null;
    if (Date.now() > item.expiry) {
      delete this.store[url];
      return null;
    }
    return item.data;
  },
  set(url, data, ttl = 30000) { // 30 seconds default TTL
    this.store[url] = {
      data,
      expiry: Date.now() + ttl
    };
  },
  clear() {
    this.store = {};
  }
};

// Interceptor to clear cache on mutations
api.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase();
  if (method && method !== 'get') {
    if (API_DEBUG) console.log(`🧹 [API Cache Clear] Due to mutation request: ${config.method.toUpperCase()} ${config.url}`);
    apiCache.clear();
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Wrap api.get to implement caching
const originalGet = api.get;
api.get = async function (url, config = {}) {
  const useCache = config.cache !== false;
  if (useCache) {
    const cachedData = apiCache.get(url);
    if (cachedData) {
      if (API_DEBUG) console.log(`⚡ [API Cache Hit] ${url}`);
      return { data: cachedData, fromCache: true };
    }
  }
  
  const response = await originalGet.call(this, url, config);
  
  if (useCache) {
    apiCache.set(url, response.data);
  }
  return response;
};

// ── Direct-to-S3 upload (presigned POST) ─────────────────────────────────────
// Uploads a browser File straight to S3 - the bytes never pass through our
// server. Returns the storage key to persist, or null if direct upload isn't
// available (caller can fall back). Requires the bucket CORS rule.
export async function uploadDocumentToS3(file, kind = 'documents') {
  if (!file) return null;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 5MB maximum limit.`);
  }

  let presign;
  try {
    const res = await api.post('/api/uploads/presign/', {
      kind,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
    });
    presign = res.data;
  } catch (err) {
    if ([409, 403, 404].includes(err.response?.status)) return null; // direct upload unavailable / fall back to server upload
    return null;
  }

  if (!presign || !presign.url) return null;

  const { key, url, fields } = presign;
  const form = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => form.append(k, v));
  form.append('file', file); // file part MUST be last

  try {
    // Plain fetch (no auth header / credentials) - S3 rejects extra headers.
    const s3Res = await fetch(url, { method: 'POST', body: form });
    if (!(s3Res.status === 204 || s3Res.status === 201 || s3Res.ok)) {
      console.warn(`Direct S3 upload failed (${s3Res.status}), will fall back to server storage.`);
      return null;
    }
    return key;
  } catch (fetchErr) {
    console.warn('Direct S3 upload network/CORS error, falling back to server storage:', fetchErr);
    return null;
  }
}

export default api;
